import { Fragment, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ChevronDown, Copy, Download, Pencil, Plus, Search, Trash2 } from 'lucide-react';
import { fmtDateTime, fmtDec, fmtDur } from '@foodstyles/shared';
import { useAuth } from '../../context/AuthContext';
import { useBootstrap } from '../../hooks/useBootstrap';
import { useToast } from '../../context/ToastContext';
import { useCopyEntryToMe, useDeleteEntry } from '../../hooks/useEntries';
import { ApiError, apiGet, qs } from '../../lib/api';
import { downloadReportCsv } from '../../lib/download';
import { cn } from '../../lib/cn';
import { Btn, Card, EmptyState, Spinner } from '../../components/primitives';
import { MultiSelect } from '../../components/MultiSelect';
import { RangePicker, useDateRange } from '../../components/RangePicker';
import { AddTimeModal } from './AddTimeModal';
import { CalendarView } from './CalendarView';
import { EntryEditModal } from '../tracker/EntryEditModal';
import type { DetailedResponse, DetailedRow, EntryDto, ReportFilters, SummaryResponse, TreeNodeDto } from '../../types/api';

const DIMS = ['User', 'Project', 'Task', 'Team', 'Description'];
const NODE_BG = ['bg-white dark:bg-slate-800', 'bg-slate-50/80 dark:bg-slate-800/60', 'bg-slate-100/60 dark:bg-slate-700/60'];

export function ReportsScreen() {
  const { permissions, user } = useAuth();
  const boot = useBootstrap();
  const toast = useToast();
  const copyToMe = useCopyEntryToMe();
  const deleteEntry = useDeleteEntry();
  const range = useDateRange('month');
  const { from, to } = range.range;

  const [tab, setTab] = useState<'summary' | 'detailed' | 'calendar'>('summary');
  const [members, setMembers] = useState<string[]>([]);
  const [memberShow, setMemberShow] = useState<'all' | 'active' | 'inactive'>('active');
  const [teams, setTeams] = useState<string[]>([]);
  const [projects, setProjects] = useState<string[]>([]);
  const [tasks, setTasks] = useState<string[]>([]);
  const [search, setSearch] = useState('');
  const [dims, setDims] = useState<string[]>(['User', 'Project', 'Description']);
  const [addOpen, setAddOpen] = useState(false);
  const [editing, setEditing] = useState<EntryDto | null>(null);

  // Wire format: comma-joined id lists; empty string = no filter.
  const filters: ReportFilters = {
    from,
    to,
    member: members.join(','),
    team: teams.join(','),
    project: projects.join(','),
    task: tasks.join(','),
    search,
  };
  const activeDims = dims.filter((d) => d && d !== 'None');
  const query = qs({ ...filters, groupBy: tab === 'summary' ? activeDims.join(',') : undefined });

  const summary = useQuery({
    queryKey: ['report', 'summary', filters, activeDims.join(',')],
    queryFn: () => apiGet<SummaryResponse>(`/api/reports/summary${query}`),
    enabled: tab === 'summary',
  });
  const detailed = useQuery({
    queryKey: ['report', 'detailed', filters],
    queryFn: () => apiGet<DetailedResponse>(`/api/reports/detailed${qs(filters)}`),
    enabled: tab === 'detailed',
  });

  const totalMs = (tab === 'summary' ? summary.data?.totalMs : detailed.data?.totalMs) ?? 0;
  const count = (tab === 'summary' ? summary.data?.count : detailed.data?.count) ?? 0;

  // Tasks selectable once at least one project is picked (union of their tasks).
  const taskOptions = (boot.data?.tasks ?? []).filter((t) => projects.includes(t.projectId));

  const onProjectsChange = (v: string[]) => {
    setProjects(v);
    // Drop task selections that no longer belong to a selected project.
    const allowed = new Set((boot.data?.tasks ?? []).filter((t) => v.includes(t.projectId)).map((t) => t.id));
    setTasks((prev) => prev.filter((id) => allowed.has(id)));
  };

  const drillInto = (node: TreeNodeDto, pathDims: string[]) => {
    let m = '',
      tm = '',
      p = '',
      tk = '',
      s = '';
    pathDims.forEach((dim) => {
      if (dim === 'User') m = node.sample.userId;
      if (dim === 'Team') tm = node.sample.teamId;
      if (dim === 'Project') p = node.sample.projectId;
      if (dim === 'Task') {
        p = node.sample.projectId;
        tk = node.sample.taskId;
      }
      if (dim === 'Description') s = node.sample.description;
    });
    setMembers(m ? [m] : []);
    setTeams(tm ? [tm] : []);
    setProjects(p ? [p] : []);
    setTasks(tk ? [tk] : []);
    setSearch(s);
    setTab('detailed');
  };

  const editRow = (r: DetailedRow) => {
    setEditing({
      id: r.id,
      userId: r.userId,
      projectId: r.projectId,
      taskId: r.taskId,
      description: r.description,
      start: r.start,
      stop: r.stop,
      projectName: r.project,
      taskName: r.task,
      projectArchived: false,
      taskArchived: false,
    });
  };

  const onExport = () =>
    downloadReportCsv(qs(filters)).catch(() => toast('Export failed.'));

  const onDeleteRow = (r: DetailedRow) => {
    const ok = confirm(
      `Delete this entry?\n\n"${r.description}"\n${r.date} · ${fmtDec(r.durMs)} h — logged by ${r.user}\n\nThis cannot be undone.`,
    );
    if (!ok) return;
    deleteEntry.mutate(r.id, {
      onSuccess: () => toast('Entry deleted.', 'success'),
      onError: (e) => toast(e instanceof ApiError ? e.message : 'Could not delete the entry.'),
    });
  };

  // "I was there too": duplicate a teammate's entry into my own log (confirmed).
  const onCopyToMe = (r: DetailedRow) => {
    const ok = confirm(
      `Copy this entry to your own log?\n\n` +
        `"${r.description}"\n${r.date} · ${fmtDec(r.durMs)} h — logged by ${r.user}\n\n` +
        `A new entry with the same time, project and task will be added to YOUR log.`,
    );
    if (!ok) return;
    copyToMe.mutate(r.id, {
      onSuccess: () => toast('Added to your log.', 'success'),
      onError: (e) => toast(e instanceof ApiError ? e.message : 'Could not copy the entry.'),
    });
  };

  return (
    <div className="mx-auto max-w-6xl space-y-4">
      <div className="flex items-center gap-2">
        {(['summary', 'detailed', 'calendar'] as const).map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={cn(
              'rounded-lg px-3 py-1.5 text-sm font-semibold capitalize',
              tab === t ? 'bg-ink text-white' : 'text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700',
            )}
          >
            {t}
          </button>
        ))}
      </div>

      {tab === 'calendar' ? (
        <CalendarView />
      ) : (
        <>
      <Card className="space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          <RangePicker state={range} exclude={['year']} />
        </div>
        <div className="grid grid-cols-2 gap-2 lg:grid-cols-5">
          <MultiSelect
            placeholder="All members"
            value={members}
            onChange={setMembers}
            segments={[
              { id: 'all', label: 'All' },
              { id: 'active', label: 'Active' },
              { id: 'inactive', label: 'Inactive' },
            ]}
            segment={memberShow}
            onSegmentChange={(s) => setMemberShow(s as typeof memberShow)}
            options={(boot.data?.users ?? []).map((u) => ({
              value: u.id,
              // status is 'Active' | 'Disabled' | 'Pending'; anything non-active is "inactive".
              label: `${u.name}${u.status !== 'Active' ? ` (${u.status.toLowerCase()})` : ''}`,
              group: u.status === 'Active' ? 'active' : 'inactive',
            }))}
          />
          <MultiSelect
            placeholder="All teams"
            value={teams}
            onChange={setTeams}
            options={(boot.data?.teams ?? []).map((t) => ({ value: t.id, label: t.name }))}
          />
          <MultiSelect
            placeholder="All projects"
            value={projects}
            onChange={onProjectsChange}
            options={(boot.data?.projects ?? []).map((p) => ({ value: p.id, label: p.name }))}
          />
          <MultiSelect
            placeholder="All tasks"
            disabledLabel="Pick project"
            disabled={projects.length === 0}
            value={tasks}
            onChange={setTasks}
            options={taskOptions.map((t) => ({ value: t.id, label: t.name }))}
          />
          <div className="relative">
            <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              className="w-full rounded-lg border border-slate-200 dark:border-slate-700 py-2 pl-8 pr-2.5 text-sm"
              placeholder="Search description"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>
      </Card>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-slate-500 dark:text-slate-400">
          {count} entries · Total <span className="font-mono font-semibold text-slate-700 dark:text-slate-200">{fmtDec(totalMs)}</span> h · {fmtDur(totalMs)}
        </p>
        <div className="flex items-center gap-2">
          {permissions?.manageUsers && (
            <Btn variant="secondary" onClick={() => setAddOpen(true)}>
              <Plus size={16} /> Add time
            </Btn>
          )}
          <Btn variant="primary" onClick={onExport} disabled={count === 0}>
            <Download size={16} /> Export CSV
          </Btn>
          <Btn variant="secondary" disabled title="Coming soon in production">
            PDF
          </Btn>
          <Btn variant="secondary" disabled title="Coming soon in production">
            Excel
          </Btn>
        </div>
      </div>

      {tab === 'summary' ? (
        <Card pad={false}>
          <div className="flex flex-wrap items-center gap-2 border-b border-slate-100 dark:border-slate-700 px-4 py-3">
            <span className="text-sm font-semibold text-slate-500 dark:text-slate-400">Group by:</span>
            {[0, 1, 2].map((level) => (
              <select
                key={level}
                className="rounded-lg border border-slate-200 dark:border-slate-700 px-2.5 py-1.5 text-sm"
                value={dims[level] ?? 'None'}
                onChange={(e) => {
                  const next = [...dims];
                  next[level] = e.target.value;
                  setDims(next);
                }}
              >
                {level > 0 && <option value="None">None</option>}
                {DIMS.filter((d) => !dims.slice(0, level).includes(d)).map((d) => (
                  <option key={d} value={d}>
                    {d}
                  </option>
                ))}
              </select>
            ))}
          </div>
          {summary.isLoading ? (
            <div className="flex justify-center py-10">
              <Spinner />
            </div>
          ) : (summary.data?.tree.length ?? 0) === 0 ? (
            <EmptyState>No entries match these filters.</EmptyState>
          ) : (
            <div>
              {summary.data!.tree.map((n) => (
                <GroupNode key={n.label} node={n} depth={0} path={[n.label]} dims={activeDims} onDrill={drillInto} />
              ))}
            </div>
          )}
        </Card>
      ) : (
        <Card pad={false} className="overflow-x-auto">
          {detailed.isLoading ? (
            <div className="flex justify-center py-10">
              <Spinner />
            </div>
          ) : (detailed.data?.rows.length ?? 0) === 0 ? (
            <EmptyState>No entries match these filters.</EmptyState>
          ) : (
            <table className="w-full min-w-[920px] text-sm">
              <thead>
                <tr className="border-b border-slate-100 dark:border-slate-700 text-left text-[11px] uppercase tracking-wide text-slate-400">
                  <th className="px-4 py-2 font-semibold">Date</th>
                  <th className="px-3 py-2 font-semibold">User</th>
                  <th className="px-3 py-2 font-semibold">Team</th>
                  <th className="px-3 py-2 font-semibold">Project</th>
                  <th className="px-3 py-2 font-semibold">Task</th>
                  <th className="px-3 py-2 font-semibold">Description</th>
                  <th className="px-3 py-2 font-semibold">Start</th>
                  <th className="px-3 py-2 font-semibold">Stop</th>
                  <th className="px-3 py-2 text-right font-semibold">Dur</th>
                  <th className="px-3 py-2" />
                </tr>
              </thead>
              <tbody>
                {detailed.data!.rows.map((r) => (
                  <tr key={r.id} className="border-b border-slate-50 dark:border-slate-700">
                    <td className="whitespace-nowrap px-4 py-2 text-slate-600 dark:text-slate-300">{r.date}</td>
                    <td className="px-3 py-2 text-slate-700 dark:text-slate-200">{r.user}</td>
                    <td className="px-3 py-2 text-slate-500 dark:text-slate-400">{r.team}</td>
                    <td className="px-3 py-2 text-slate-600 dark:text-slate-300">{r.project}</td>
                    <td className="px-3 py-2 text-slate-600 dark:text-slate-300">{r.task}</td>
                    <td className="max-w-[220px] truncate px-3 py-2 text-slate-700 dark:text-slate-200" title={r.description}>
                      {r.description}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2 font-mono tabular-nums text-slate-500 dark:text-slate-400">{fmtDateTime(r.start)}</td>
                    <td className="whitespace-nowrap px-3 py-2 font-mono tabular-nums text-slate-500 dark:text-slate-400">
                      {r.stop != null ? fmtDateTime(r.stop) : '—'}
                    </td>
                    <td className="px-3 py-2 text-right font-mono tabular-nums text-slate-700 dark:text-slate-200">{fmtDec(r.durMs)}</td>
                    <td className="px-3 py-2">
                      <div className="flex items-center justify-end gap-0.5">
                        {user && r.userId !== user.id && (
                          <button
                            onClick={() => onCopyToMe(r)}
                            disabled={copyToMe.isPending}
                            title="Copy to my log (I was there too)"
                            aria-label="Copy to my log"
                            className="rounded p-1 text-slate-400 hover:bg-[#FF5A3C]/10 dark:hover:bg-[#FF5A3C]/20 hover:text-[#D8341A] dark:hover:text-[#FF8A6B] disabled:opacity-40"
                          >
                            <Copy size={14} />
                          </button>
                        )}
                        {r.canEdit && (
                          <button onClick={() => editRow(r)} aria-label="Edit entry" className="rounded p-1 text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700 hover:text-slate-600 dark:hover:text-slate-300">
                            <Pencil size={14} />
                          </button>
                        )}
                        {r.canDelete && (
                          <button
                            onClick={() => onDeleteRow(r)}
                            disabled={deleteEntry.isPending}
                            title={user && r.userId !== user.id ? 'Delete entry (Admin)' : 'Delete entry'}
                            aria-label="Delete entry"
                            className="rounded p-1 text-slate-400 hover:bg-rose-50 dark:hover:bg-rose-500/10 hover:text-rose-600 dark:hover:text-rose-400 disabled:opacity-40"
                          >
                            <Trash2 size={14} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>
      )}
        </>
      )}

      <AddTimeModal open={addOpen} onClose={() => setAddOpen(false)} />
      <EntryEditModal entry={editing} onClose={() => setEditing(null)} />
    </div>
  );
}

function GroupNode({
  node,
  depth,
  path,
  dims,
  onDrill,
}: {
  node: TreeNodeDto;
  depth: number;
  path: string[];
  dims: string[];
  onDrill: (node: TreeNodeDto, pathDims: string[]) => void;
}) {
  const [open, setOpen] = useState(false);
  const hasKids = !!node.children && node.children.length > 0;
  const pathDims = dims.slice(0, path.length);
  return (
    <Fragment>
      <div
        onClick={() => (hasKids ? setOpen((o) => !o) : onDrill(node, pathDims))}
        className={cn(
          'group flex cursor-pointer items-center gap-2.5 border-b border-slate-100 dark:border-slate-700 py-2.5 pr-4 hover:bg-[#FF5A3C]/[0.04] dark:hover:bg-[#FF5A3C]/10',
          NODE_BG[Math.min(depth, 2)],
        )}
        style={{ paddingLeft: 16 + depth * 26 }}
      >
        {hasKids ? (
          <ChevronDown size={15} className={cn('shrink-0 text-slate-400 transition-transform', !open && '-rotate-90')} />
        ) : (
          <span className="w-[15px] shrink-0" />
        )}
        <span className="inline-flex h-5 min-w-[20px] shrink-0 items-center justify-center rounded bg-slate-200/70 dark:bg-slate-700/70 px-1 text-[11px] font-bold tabular-nums text-slate-500 dark:text-slate-400">
          {node.count}
        </span>
        <span className={cn('truncate text-sm', depth === 0 ? 'font-semibold text-slate-800 dark:text-slate-100' : 'font-medium text-slate-700 dark:text-slate-200')}>
          {node.label}
        </span>
        <button
          onClick={(e) => {
            e.stopPropagation();
            onDrill(node, pathDims);
          }}
          title="View these entries in Detailed"
          className="rounded p-1 text-slate-300 dark:text-slate-600 opacity-0 transition-opacity hover:bg-slate-200/60 dark:hover:bg-slate-700/60 hover:text-slate-600 dark:hover:text-slate-300 group-hover:opacity-100"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round">
            <path d="M7 17 17 7M9 7h8v8" />
          </svg>
        </button>
        <span className="ml-auto shrink-0 font-mono text-sm tabular-nums text-slate-700 dark:text-slate-200">{fmtDec(node.ms)}</span>
      </div>
      {hasKids &&
        open &&
        node.children!.map((c) => (
          <GroupNode key={c.label} node={c} depth={depth + 1} path={[...path, c.label]} dims={dims} onDrill={onDrill} />
        ))}
    </Fragment>
  );
}
